import React, { useEffect, useMemo, useState } from 'react';
import ReactSelect from 'react-select';
import { bulkAssignAccountManager, staffOptions } from './accountManagers';

const BTN_BASE = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_PRIMARY = `${BTN_BASE} bg-[#FF5733] text-white hover:bg-[#e64520]`;
const BTN_SECONDARY = `${BTN_BASE} bg-gray-100 text-gray-700 hover:bg-gray-200`;
const BTN_LINK = 'text-xs font-semibold text-[#FF5733] hover:underline disabled:text-gray-300 disabled:no-underline';

const selectStyles = {
    control: (base) => ({ ...base, minHeight: '38px', borderColor: '#d1d5db', '&:hover': { borderColor: '#9ca3af' } }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
        color: state.isSelected ? 'white' : 'black',
        cursor: 'pointer'
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#fff1ec' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

/**
 * Assign one account manager to several cars at once.
 * `cars` is every car on the page; `filteredCars` is what the list currently shows.
 */
const AssignAccountManagerModal = ({ isOpen, onClose, onAssigned, token, staff, cars, filteredCars }) => {
    const [managerId, setManagerId] = useState(null);
    const [plates, setPlates] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setManagerId(null);
            setPlates([]);
            setError('');
        }
    }, [isOpen]);

    const carOptions = useMemo(() => (cars || []).map((car) => ({
        value: car.plate_no,
        label: `${car.plate_no} · ${car.account_manager_name || 'Unassigned'}`
    })), [cars]);

    const unassignedPlates = useMemo(() => (cars || []).filter((c) => !c.account_manager_id).map((c) => c.plate_no), [cars]);
    const filteredPlates = useMemo(() => (filteredCars || []).map((c) => c.plate_no), [filteredCars]);

    const addPlates = (list) => setPlates((prev) => Array.from(new Set([...prev, ...list])));

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!managerId) {
            setError('Pick the account manager to assign.');
            return;
        }
        if (plates.length === 0) {
            setError('Pick at least one car.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const result = await bulkAssignAccountManager(token, { plateNos: plates, accountManagerId: managerId });
            onAssigned(result.message || 'Account manager assigned.');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not assign the account manager. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const managerOptions = staffOptions(staff);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
        >
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="assign-manager-title">
                <h2 id="assign-manager-title" className="text-xl font-bold text-[#2a2e30]">Assign Account Manager</h2>
                <p className="mt-1 text-sm text-gray-500">The change is recorded in each car's account manager history.</p>

                <div className="mt-5 space-y-4">
                    <div>
                        <label htmlFor="bulk-account-manager" className="mb-1 block text-sm font-medium text-gray-700">
                            Account Manager <span className="text-red-500">*</span>
                        </label>
                        <ReactSelect
                            inputId="bulk-account-manager"
                            options={managerOptions}
                            value={managerOptions.find((o) => o.value === managerId) || null}
                            onChange={(option) => setManagerId(option ? option.value : null)}
                            placeholder="Select Account Manager"
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                        />
                    </div>

                    <div>
                        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                            <label htmlFor="bulk-cars" className="text-sm font-medium text-gray-700">
                                Cars <span className="text-red-500">*</span>
                                <span className="ml-2 text-xs font-normal text-gray-500">{plates.length} selected</span>
                            </label>
                            <div className="flex flex-wrap gap-3">
                                <button type="button" className={BTN_LINK} disabled={!unassignedPlates.length} onClick={() => addPlates(unassignedPlates)}>
                                    + Unassigned ({unassignedPlates.length})
                                </button>
                                <button type="button" className={BTN_LINK} disabled={!filteredPlates.length} onClick={() => addPlates(filteredPlates)}>
                                    + Cars in current list ({filteredPlates.length})
                                </button>
                                <button type="button" className={BTN_LINK} disabled={!plates.length} onClick={() => setPlates([])}>
                                    Clear
                                </button>
                            </div>
                        </div>
                        <ReactSelect
                            inputId="bulk-cars"
                            isMulti
                            closeMenuOnSelect={false}
                            options={carOptions}
                            value={carOptions.filter((o) => plates.includes(o.value))}
                            onChange={(selected) => setPlates((selected || []).map((o) => o.value))}
                            placeholder="Search plate numbers"
                            styles={{ ...selectStyles, valueContainer: (base) => ({ ...base, maxHeight: '160px', overflowY: 'auto' }) }}
                            menuPortalTarget={document.body}
                        />
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button type="button" className={BTN_SECONDARY} onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="button" className={BTN_PRIMARY} onClick={handleSave} disabled={saving}>
                        {saving ? 'Assigning…' : `Assign to ${plates.length} car${plates.length === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignAccountManagerModal;
